import {
  BodyBox,
  Button,
  defaultModalTransitionMs,
  Icon,
  Notice,
  pxToRem,
  scrollToFirstError,
  Text,
  useIsDesktop,
} from '@lbcamden/lib-react';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { DateTime } from 'luxon';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';

import {
  useCurrentUser,
  usePcfraDraft,
  usePcfraPerson,
  usePcfraPropertyDetails,
  usePcfraReferrer,
  useProperty,
  useRoleInformation,
} from '~/client/hooks/storeHooks';
import { toPcrfaPropertyPage } from '~/client/utils/navigation';
import { PcfraDraftValues } from '~/common/types/pcfra';
import { PcfraStatus } from '~/common/types/people';

import { PageTitle } from '../../common/PageTitle';
import { PersonHeader } from '../../shared/PageHeader';
import { LoadInNotFoundPage } from '../LoadInNotFoundPage';
import { getInitialFormValues as getContactDetailsFormValues } from '../PersonPage/ContactDetailsEditPage/helpers';
import { pcfraConfig, pcfraSections } from './config';
import {
  generatePcfraReference,
  getPrevNextSection,
  getSectionCompletion,
  nextSectionButtonLabel,
} from './helpers';
import { ButtonsContainer, Container } from './PcfraCreateEditPage.styled';
import { ClutterScale } from './sections/ClutterScale';
import { SectionHeading } from './sections/common.styled';
import { CompletionActionNotice } from './sections/CompletionActionNotice';
import { SectionSelector } from './sections/SectionSelector';
import { PcfraSection, SectionCompletion } from './types';
import { usePcfraActions } from './usePcfraActions';
import { usePcfraConfirmationDialogs } from './usePcfraConfirmationDialogs';

export const PcfraCreateEditPage: FunctionComponent = () => {
  const pendingSectionRef = useRef<PcfraSection>(undefined);
  const firstIncompleteSectionRef = useRef<PcfraSection>(undefined);

  const {
    propertyId: propertyIdParam,
    personId: personIdParam,
    draftId: draftIdParam = '',
  } = useParams();
  const propertyId = parseInt(propertyIdParam!);
  const personId = parseInt(personIdParam!);

  const isDesktop = useIsDesktop();

  const user = useCurrentUser();
  const { isPcfraManager } = useRoleInformation();

  const property = useProperty(propertyId);
  const propertyDetails = usePcfraPropertyDetails(propertyId);
  const person = usePcfraPerson(personId);
  const referrer = usePcfraReferrer(personId);
  const existingDraft = usePcfraDraft(draftIdParam);

  const [updatedDraftValues, setUpdatedDraftValues] = useState<PcfraDraftValues>(
    existingDraft?.values || {},
  );
  const [currentSection, setCurrentSection] = useState<PcfraSection>();
  const [sectionCompletion, setSectionCompletion] = useState<SectionCompletion>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showSectionErrors, setShowSectionErrors] = useState(false);
  const [showAllErrors, setShowAllErrors] = useState(false);

  const { SectionForm } = currentSection ? pcfraConfig[currentSection] : {};
  const { prevSection, nextSection } = getPrevNextSection(currentSection);
  const incompleteSections = pcfraSections.filter((section) => !sectionCompletion[section]);
  const isComplete = !incompleteSections.length;
  const isPrimaryAuthor = existingDraft?.createdBy && user?.id === existingDraft.createdBy.id;
  const canApprove = isComplete && (isPcfraManager || !isPrimaryAuthor);
  const hasSubmitted = person?.pcfraStatus === PcfraStatus.Submitted;
  const hasActivePcfra = person?.pcfraStatus === PcfraStatus.Active;

  const { saveDraft, submitForApproval, approve, goToPcfraPropertyPage, isApproving } =
    usePcfraActions({
      propertyId,
      personId,
      existingDraft,
      updatedDraftValues,
      hasChanges,
      canApprove,
      currentSection,
    });

  const prefillDetails = () =>
    setUpdatedDraftValues((current) =>
      merge<PcfraDraftValues, PcfraDraftValues, PcfraDraftValues>(
        { ...current },
        {
          details: {
            // Always show the currently stored floor level, contact details and referrer
            floorLevel: propertyDetails?.floorLevel,
            contactDetails: person && getContactDetailsFormValues(person),
            referrerEmailAddress: referrer?.emailAddress,
            // Ensure createdBy field aligns with the draft record property
            createdBy: existingDraft?.createdBy?.name || user?.name,
          },
        },
        !existingDraft
          ? {
              details: {
                pcfraReference: generatePcfraReference(),
                createdBy: user?.name,
                uprn: property?.uprn,
                dateOfVisit: DateTime.now().toISODate(),
              },
            }
          : {},
      ),
    );

  // Timeout is primarily to allow time for error states to be rendered on section change
  const showFirstError = () => setTimeout(() => scrollToFirstError(), defaultModalTransitionMs);

  const confirmSectionNav = () => setCurrentSection(pendingSectionRef.current);

  const triggerShowSectionErrors = () => setShowSectionErrors(true);

  const triggerShowAllErrors = () => {
    setCurrentSection(firstIncompleteSectionRef.current);
    setShowAllErrors(true);
  };

  const { sectionNavDialog, finishDialog, submitDialog, approveDialog } =
    usePcfraConfirmationDialogs({
      sectionNavActions: {
        onConfirm: confirmSectionNav,
        onCancel: showSectionErrors ? showFirstError : triggerShowSectionErrors,
      },
      finishActions: { onConfirm: goToPcfraPropertyPage, onCancel: triggerShowAllErrors },
      submitActions: { onConfirm: submitForApproval, onCancel: goToPcfraPropertyPage },
      approveActions: { onConfirm: approve, onCancel: goToPcfraPropertyPage },
      hasSubmitted,
    });

  const onSectionFormUpdated = (update: Partial<PcfraDraftValues>) =>
    setUpdatedDraftValues((current) => ({ ...current, ...update }));

  const saveAndChangeSection = (section?: PcfraSection) => {
    saveDraft();

    const isDifferentSection = section !== currentSection;
    const hasIncompleteCurrentSection = currentSection && !sectionCompletion[currentSection];

    if (isDifferentSection && hasIncompleteCurrentSection) {
      pendingSectionRef.current = section;
      sectionNavDialog.show();
    } else {
      setCurrentSection(section);
    }
  };

  const onSectionChange = () => {
    const shouldShowError = showAllErrors && currentSection && !sectionCompletion[currentSection];

    if (shouldShowError) {
      showFirstError();
    } else {
      window.scrollTo(0, 0);
    }
    setShowSectionErrors(false);
  };

  const goBack = () => {
    if (currentSection) {
      saveAndChangeSection(prevSection);
    } else {
      void goToPcfraPropertyPage();
    }
  };

  const saveAndFinish = () => {
    saveDraft();

    if (incompleteSections.length) {
      firstIncompleteSectionRef.current = incompleteSections[0];
      finishDialog.show();
    } else if (isPcfraManager) {
      approveDialog.show();
    } else {
      submitDialog.show();
    }
  };

  const saveAndContinue = () => {
    if (nextSection) {
      saveAndChangeSection(nextSection);
    } else {
      saveAndFinish();
    }
  };

  const draftHasChanges = () => !isEqual(existingDraft?.values, updatedDraftValues);

  useEffect(() => {
    if (existingDraft && draftHasChanges()) {
      setUpdatedDraftValues(existingDraft.values);
    }
    setHasChanges(false);
    prefillDetails();
  }, [existingDraft, person, propertyDetails, referrer]);

  useEffect(() => {
    setSectionCompletion(getSectionCompletion(updatedDraftValues));

    if (!hasChanges) {
      setHasChanges(draftHasChanges());
    }
  }, [updatedDraftValues]);

  useEffect(() => {
    if (showSectionErrors || showAllErrors) {
      showFirstError();
    }
  }, [showSectionErrors, showAllErrors]);

  useEffect(onSectionChange, [currentSection]);

  if (!property || !person) {
    return <LoadInNotFoundPage />;
  }

  return (
    <BodyBox>
      <PageTitle title={existingDraft ? 'Edit PCFRA' : 'New PCFRA'} />
      <PersonHeader person={person} />

      <Container css={!isDesktop && !!currentSection && { paddingBottom: 0 }}>
        <Text variant="h2">Person Centred Fire Risk Assessment</Text>
        {!currentSection && !isComplete && (
          <Text>{`Please complete this form to determine the resident's fire risk.`}</Text>
        )}
        {isComplete && !hasActivePcfra && (
          <CompletionActionNotice
            hasSubmitted={hasSubmitted}
            canApprove={canApprove}
            isApproving={isApproving}
            onClickApprove={approve}
            onClickSubmit={submitForApproval}
          />
        )}
        {hasActivePcfra && (
          <Notice infoWithIcon>
            This person has an active PCFRA.{' '}
            <Link css={{ alignSelf: 'flex-start' }} to={toPcrfaPropertyPage(propertyId, personId)}>
              View
            </Link>
          </Notice>
        )}
        <SectionSelector
          collapsible={!isDesktop && !!currentSection}
          sectionCompletion={sectionCompletion}
          isViewingErrors={showAllErrors}
          disabled={hasActivePcfra}
          onClickBackToStart={currentSection && (() => saveAndChangeSection(undefined))}
          onClickSection={saveAndChangeSection}
        />
      </Container>

      {!hasActivePcfra && (
        <>
          {!currentSection && (
            <Container>
              <SectionHeading css={{ margin: 0 }}>Additional resources:</SectionHeading>
              <Text>
                The following resources may be useful whilst completing the Person Centred Fire Risk
                Assessment.
              </Text>
              <ClutterScale />
            </Container>
          )}

          {SectionForm && (
            <SectionForm
              draftValues={existingDraft?.values}
              showErrors={showSectionErrors || showAllErrors}
              onChange={onSectionFormUpdated}
            />
          )}

          <ButtonsContainer>
            <Button $variant="secondary" onClick={goBack}>
              <Icon icon="ui/arrow-light" width={pxToRem(14)} />
              Back
            </Button>
            <Button onClick={saveAndContinue}>
              {nextSectionButtonLabel(currentSection, nextSection, hasChanges)}
              {nextSection && <Icon $flipX icon="ui/arrow-light" width={pxToRem(14)} />}
            </Button>
          </ButtonsContainer>
        </>
      )}

      {sectionNavDialog.confirmationDialog}
      {finishDialog.confirmationDialog}
      {submitDialog.confirmationDialog}
      {approveDialog.confirmationDialog}
    </BodyBox>
  );
};
